import {
  COUNTRY,
  COUNTRY_CODES,
  MOBILE_PHONE,
  MOBILE_PHONE_LENGTH,
} from '@/config/country';
import {
  findPhoneCountry,
  PHONE_COUNTRIES_BY_DIAL_LENGTH,
  type PhoneCountry,
} from '@/config/phoneCountries';

const { prefix, countryCode } = MOBILE_PHONE;

/**
 * Uruguay escribe el celular con un 0 de tránsito (`09…`) que NO va en el
 * número internacional; Perú no lo usa. De ahí sale el largo esperado en
 * formato internacional.
 */
const HAS_TRUNK_ZERO = prefix.startsWith('0');
const INTL_LENGTH =
  countryCode.length + MOBILE_PHONE_LENGTH - (HAS_TRUNK_ZERO ? 1 : 0);

const INTERNATIONAL_INPUT = /^[\s(]*(\+|00)/;

const LOCAL_FORMAT_COUNTRIES: readonly string[] = [
  COUNTRY,
  ...COUNTRY_CODES.filter((code) => code !== COUNTRY),
];

/** Celular elegido en el formulario: país del selector y dígitos tipeados. */
export interface MobilePhoneValue {
  country: string;
  number: string;
}

export const emptyMobilePhone = (): MobilePhoneValue => ({
  country: COUNTRY,
  number: '',
});

const MAX_PHONE_DIGITS = 15;

const onlyDigits = (value: string): string => value.replace(/\D/g, '');

const withoutInternationalPrefix = (digits: string): string =>
  digits.replace(/^00/, '');

const matchNational = (digits: string, country: PhoneCountry): string | null =>
  new RegExp(
    `^(?:${country.trunkPrefix})?${country.mobilePrefix}(\\d{${country.length}})$`
  ).exec(digits)?.[1] ?? null;

const matchInternational = (digits: string): MobilePhoneValue | null => {
  for (const country of PHONE_COUNTRIES_BY_DIAL_LENGTH) {
    if (!digits.startsWith(country.dialCode)) continue;
    const national = digits.slice(country.dialCode.length);
    if (
      national.startsWith(country.mobilePrefix) &&
      national.length === country.mobilePrefix.length + country.length
    ) {
      return {
        country: country.code,
        number: national.slice(country.mobilePrefix.length),
      };
    }
  }
  return null;
};

const matchLocal = (digits: string): MobilePhoneValue | null => {
  for (const code of LOCAL_FORMAT_COUNTRIES) {
    const country = findPhoneCountry(code);
    const number = country && matchNational(digits, country);
    if (number) return { country: code, number };
  }
  return null;
};

const toTypedNumber = (digits: string, country?: PhoneCountry): string => {
  const capped = digits.slice(0, MAX_PHONE_DIGITS);
  return (country && matchNational(capped, country)) ?? capped;
};

/**
 * Lee lo que se tipea o se pega en el campo del celular.
 *
 * Si trae `+` o `00` adelante, el número dice su país: si el prefijo está en
 * la lista se cambia el selector solo; si todavía no se reconoce (se está
 * tipeando `+59…`) se deja el texto como está. El número completo en formato
 * local (`099 123 456` con Uruguay elegido) queda en los dígitos que van
 * después del prefijo fijo. Lo demás no se recorta: si se tipea un celular
 * antes de elegir el país, cambiar el país después no obliga a reescribirlo.
 */
export function readMobilePhoneInput(
  raw: string,
  current: MobilePhoneValue
): MobilePhoneValue {
  if (!INTERNATIONAL_INPUT.test(raw)) {
    return {
      ...current,
      number: toTypedNumber(onlyDigits(raw), findPhoneCountry(current.country)),
    };
  }

  const digits = withoutInternationalPrefix(onlyDigits(raw));
  const country = PHONE_COUNTRIES_BY_DIAL_LENGTH.find((candidate) =>
    digits.startsWith(candidate.dialCode)
  );

  if (!country) return { ...current, number: `+${digits}` };

  return {
    country: country.code,
    number: toTypedNumber(digits.slice(country.dialCode.length), country),
  };
}

/** Motivo por el que el celular no sirve, o `null` si está bien. */
export function mobilePhoneIssue({
  country,
  number,
}: MobilePhoneValue): string | null {
  const phoneCountry = findPhoneCountry(country);
  if (!phoneCountry) return 'Elegí el país del teléfono';
  if (!number) return 'El teléfono es obligatorio';
  if (number.startsWith('+')) {
    return 'Ese prefijo no está en la lista: elegí el país en el selector';
  }
  if (!/^\d+$/.test(number)) return 'Ingresá solo números';
  if (number.length === phoneCountry.length) return null;

  return phoneCountry.mobilePrefix
    ? `Ingresá los ${phoneCountry.length} dígitos que van después del ${phoneCountry.mobilePrefix}`
    : `El celular de ${phoneCountry.label} tiene ${phoneCountry.length} dígitos`;
}

/**
 * El celular como se manda al backend.
 *
 * El del país del panel viaja igual que siempre (`09…` en Uruguay, `9…` en
 * Perú); el de cualquier otro país, en formato internacional con `+`, que es
 * lo que el backend respeta tal cual al mandar SMS y WhatsApp.
 */
export function toFullMobilePhone({
  country,
  number,
}: MobilePhoneValue): string {
  const phoneCountry = findPhoneCountry(country);
  if (!phoneCountry || !number) return number;

  if (phoneCountry.code === COUNTRY) {
    return `${phoneCountry.trunkPrefix}${phoneCountry.mobilePrefix}${number}`;
  }

  return `+${phoneCountry.dialCode}${phoneCountry.mobilePrefix}${number}`;
}

/**
 * Descompone un celular guardado en país + dígitos, para cargarlo en el
 * selector. `null` si no se reconoce como celular de ningún país de la lista.
 */
export function parseMobilePhone(
  phone: string | null | undefined
): MobilePhoneValue | null {
  const raw = phone?.trim() ?? '';
  const digits = onlyDigits(raw);
  if (!digits) return null;

  if (INTERNATIONAL_INPUT.test(raw)) {
    return matchInternational(withoutInternationalPrefix(digits));
  }

  return matchLocal(digits) ?? matchInternational(digits);
}

/**
 * Un celular guardado, listo para el formulario. Si no se reconoce se carga
 * tal cual, para que la validación lo marque en vez de corregirlo a ciegas.
 */
export const toMobilePhoneValue = (
  phone: string | null | undefined
): MobilePhoneValue =>
  parseMobilePhone(phone) ??
  readMobilePhoneInput(phone ?? '', emptyMobilePhone());

const internationalDigits = (value: MobilePhoneValue): string => {
  const country = findPhoneCountry(value.country) as PhoneCountry;
  return `${country.dialCode}${country.mobilePrefix}${value.number}`;
};

const groupDigits = (digits: string): string => {
  if (digits.length % 3 === 1) {
    const head = digits.slice(0, -4).match(/\d{1,3}/g) ?? [];
    return [...head, digits.slice(-4)].join(' ');
  }
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

const formatInternational = (value: MobilePhoneValue): string => {
  const country = findPhoneCountry(value.country) as PhoneCountry;
  return `+${country.dialCode} ${groupDigits(
    `${country.mobilePrefix}${value.number}`
  )}`;
};

export function formatPhoneWithCountryCode(
  phone: string | null | undefined
): string | null {
  if (!phone?.trim()) return null;

  const digitsOnly = phone.replace(/\D/g, '');
  if (!digitsOnly) return null;

  const parsed = parseMobilePhone(phone);
  if (parsed) return internationalDigits(parsed);

  if (digitsOnly.startsWith(countryCode) && digitsOnly.length >= INTL_LENGTH) {
    return digitsOnly;
  }

  if (HAS_TRUNK_ZERO && digitsOnly.startsWith('0')) {
    return `${countryCode}${digitsOnly.slice(1)}`;
  }

  if (digitsOnly.length === MOBILE_PHONE_LENGTH) {
    return `${countryCode}${digitsOnly}`;
  }

  return digitsOnly;
}

/** Lleva cualquier variante guardada al celular local, sin prefijo internacional. */
function toLocalMobile(digits: string): string {
  let local = digits;

  if (local.startsWith(countryCode) && local.length >= INTL_LENGTH) {
    local = local.slice(countryCode.length);
    if (HAS_TRUNK_ZERO) local = `0${local}`;
  }

  // Número viejo guardado sin el 0 de tránsito.
  if (HAS_TRUNK_ZERO && local.length === MOBILE_PHONE_LENGTH - 1) {
    local = `0${local}`;
  }

  return local;
}

export function formatPhoneForDisplay(
  phone: string | null | undefined
): string {
  if (!phone?.trim()) return '______';

  const digits = phone.replace(/\D/g, '');
  if (!digits) return phone;

  const local = toLocalMobile(digits);

  if (local.length === MOBILE_PHONE_LENGTH && local.startsWith(prefix)) {
    // uy: 099 123 456 — pe: 998 334 305
    return `${local.slice(0, 3)} ${local.slice(3, 6)} ${local.slice(6)}`;
  }

  const parsed = parseMobilePhone(phone);
  return parsed ? formatInternational(parsed) : phone;
}
